import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button, type ButtonSize, type ButtonVariant } from '../../../components/Button';
import { Popover, usePopover } from '../../../components/Popover';
import styles from './ConfirmBeside.module.css';

/**
 * A button that asks first, in a small pop-up beside itself, never centred (CLAUDE.md §5; owner, 2026-11-02): for
 * the actions that can't be taken back — an email that has gone, a job removed. Confirming closes the pop-up and acts
 * at once; the page shows the result where the button was.
 */
export function ConfirmBeside({
  label,
  icon,
  variant = 'quiet',
  size = 'sm',
  disabled = false,
  isPending = false,
  title,
  body,
  confirmLabel,
  isDanger = false,
  onConfirm,
}: {
  readonly label: string;
  readonly icon?: LucideIcon;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly disabled?: boolean;
  readonly isPending?: boolean;
  /** The question, in bold ink. */
  readonly title: string;
  /** What exactly will happen. */
  readonly body: ReactNode;
  readonly confirmLabel: string;
  readonly isDanger?: boolean;
  readonly onConfirm: () => void;
}): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const confirm = (): void => {
    close();
    onConfirm();
  };
  return (
    <>
      <Button ref={triggerRef} variant={variant} size={size} icon={icon} disabled={disabled} isPending={isPending} aria-expanded={isOpen} onClick={toggle}>
        {label}
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={320} label={title}>
        <div className={styles.confirm}>
          <h3 className={styles.title}>{title}</h3>
          <div className={styles.body}>{body}</div>
          <div className={styles.actions}>
            <Button variant="ghost" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button variant={isDanger ? 'dangerSolid' : 'solid'} size="sm" onClick={confirm} autoFocus>
              {confirmLabel}
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
